import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';

import { CAMPAIGN_MIN_BUDGET, EUserRoleIDs } from '@feathr/blackbox';
import {
  Alert,
  Button,
  ButtonValid,
  CardV2 as Card,
  Fieldset,
  ImagePicker,
  Input,
  NumberInput,
  Well,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import UserSelect from '@feathr/extender/components/UserSelect';
import { flattenError, flattenErrors } from '@feathr/hooks';

import GracePeriodSelect from '../../GracePeriodSelect';
import EmailHealthSelect from './EmailHealthSelect';
import IndustryCategorySelect from './IndustryCategorySelect';
import { useProfilePage } from './ProfilePage.hooks';

function ProfilePage(): JSX.Element {
  const {
    account,
    t,
    localUrl,
    Users,
    selectedProduct,
    setSelectedProduct,
    hasInvalidActivePackage,
    industryCategory,
    setIndustryCategory,
    industrySubCategory,
    setIndustrySubCategory,
    setIndustryDirty,
    handleEmailHealthChange,
    handleSave,
    handleAccountManagerChange,
    handleProChange,
    handleCampaignsMaxBudgetChange,
    onPickLogo,
    errors,
    pickerOptions,
    setRightActions,
  } = useProfilePage();

  const hasUnsavedChanges = account.isDirty || !!selectedProduct;
  const hasErrors = flattenErrors(errors).length > 0;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    setRightActions(
      <ButtonValid
        disabled={hasUnsavedChanges ? undefined : true}
        errors={flattenErrors(errors)}
        onClick={() => handleSaveRef.current()}
        prefix={<FontAwesomeIcon icon={faSave} />}
      >
        {t('Save changes')}
      </ButtonValid>,
    );
  }, [hasUnsavedChanges, hasErrors]);

  return (
    <Page
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t('Manage account details, team assignments, licensing, and campaign settings.')}
      includeCurrentPageCrumb={true}
      loading={account.isPending}
      showHeader={true}
      title={t('Profile')}
    >
      <Card name={'account-information-card'}>
        <Card.Header
          description={t("Account name and logo displayed to the account's users.")}
          title={t('Account Information')}
        />
        <Card.Content addVerticalGap={true}>
          <Input
            attribute={'name'}
            label={t('Name')}
            model={account}
            type={'text'}
            validationError={flattenError(errors.name)}
          />
          <ImagePicker
            attribute={'logo'}
            helpText={t(
              "File or URL of the organization's logo image. PNG, GIF, or JPG files accepted.",
            )}
            label={t('Logo')}
            model={account}
            onPick={onPickLogo}
            pickerOptions={pickerOptions}
            validationError={flattenError(errors.logo)}
            value={account.attributes.logo}
          />
        </Card.Content>
      </Card>

      <Card name={'license-grace-period-card'}>
        <Card.Header
          description={t('Active license package and grace period overrides.')}
          title={t('License & Grace Period')}
        />
        <Card.Content addVerticalGap={true}>
          {hasInvalidActivePackage && (
            <Alert
              description={t(
                'An invalid license was found on this account. This could cause issues with accessing some features in the app.',
              )}
              title={t('Invalid license')}
              type={'warning'}
            />
          )}
          <Well
            description={account.activePackage?.name ?? t('None')}
            label={t('License')}
            layout={'horizontal'}
            name={'license-well'}
          >
            <Button link={localUrl('settings/billing/license')} target={'_blank'} type={'link'}>
              {t('View details')}
            </Button>
          </Well>
          <GracePeriodSelect onChange={setSelectedProduct} product={selectedProduct} />
        </Card.Content>
      </Card>

      <Card name={'team-card'}>
        <Card.Header
          description={t('Internal Feathr staff assigned to this account.')}
          title={t('Team')}
        />
        <Card.Content addVerticalGap={true}>
          <Fieldset direction={'column'}>
            <UserSelect
              filters={{
                global_role: EUserRoleIDs.AccountManager,
                is_archived__ne: true,
                skip_acl: true,
              }}
              label={t('Account manager')}
              onChange={handleAccountManagerChange}
              onClear={handleAccountManagerChange}
              placeholder={t('Select account manager...')}
              queryOptions={{ fetchOptions: { headers: Users.getHeaders(false) } }}
              value={account.get('csm')}
            />
            <UserSelect
              filters={{
                global_role: EUserRoleIDs.Pro,
                is_archived__ne: true,
                skip_acl: true,
              }}
              isClearable={true}
              label={t('Pro')}
              onChange={handleProChange}
              onClear={handleProChange}
              placeholder={t('Select Pro...')}
              queryOptions={{ fetchOptions: { headers: Users.getHeaders(false) } }}
              value={account.get('strategist')}
            />
          </Fieldset>
        </Card.Content>
      </Card>

      <Card name={'email-campaigns-card'}>
        <Card.Header title={t('Email & Campaigns')} />
        <Card.Content>
          <EmailHealthSelect
            label={t('Email health status')}
            onChange={handleEmailHealthChange}
            onClear={handleEmailHealthChange}
            value={account.get('email_health')}
          />
        </Card.Content>
        <Card.Content>
          <NumberInput
            helpText={t('The maximum budget allowed for a single campaign.')}
            label={t('Max campaign budget')}
            max={account.campaignsMaxBudget}
            min={CAMPAIGN_MIN_BUDGET}
            onChange={handleCampaignsMaxBudgetChange}
            prefix={'$'}
            validationError={flattenError(errors['settings.campaigns_max_budget'])}
            value={account.campaignsMaxBudget}
          />
        </Card.Content>
      </Card>

      <Card name={'industry-card'}>
        <Card.Header
          description={t('IAB industry classification used for ad targeting.')}
          title={t('Industry')}
        />
        <Card.Content addVerticalGap={true}>
          <IndustryCategorySelect
            handleCategoryChange={setIndustryCategory}
            handleSubcategoryChange={setIndustrySubCategory}
            industryCategory={industryCategory}
            industrySubCategory={industrySubCategory}
            onDirty={setIndustryDirty}
          />
        </Card.Content>
      </Card>
    </Page>
  );
}

export default observer(ProfilePage);
